import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  DEVICE_OFFLINE_CONFIRM_READS,
  DEVICE_REMOVAL_SUPPRESSION_MS,
} from "../../constants/restDataRefresh";

/** Normalises a device id for comparison (strips hyphens/spacing, lowercases). */
const normalizeDeviceId = (id: string | null | undefined): string =>
  (id ?? "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

export interface IDeviceOnlinePollingState {
  /** device_id (no hyphens) being polled */
  deviceId: string;
  /** Date.now() when polling started — used to enforce the timeout cap */
  startedAt: number;
  /**
   * How long to keep polling before declaring a timeout, in ms. Firmware-aware:
   * factory devices run a one-time OTA (which itself requires the correct
   * Wi-Fi password) before they can report in, so they get a longer window
   * than devices that just reconnect. Falls back to a default in the hook.
   */
  timeoutMs?: number;
}

export interface IClinicStateSlice {
  error: unknown;
  clinicState: IReduxLoadingState;
  clinicList: IClinic[];
  userClinic?: IClinic;
  clinicFloorList: IClinicFloor[];
  loadingFloors: number[];
  selectedFloors: null | number[];
  redirectToUserInfo: boolean;
  assignedDevice: IAssignedDevice | null;
  /**
   * Consecutive "offline" reads observed for the assigned device. Used to
   * debounce false disconnections — see `applyDeviceFetchResult`. Intentionally
   * NOT persisted (excluded from the redux-persist whitelist) so it resets on
   * every reload and never freezes a stale value.
   */
  deviceOfflineStreak: number;
  /** Non-null while the app is polling to detect the newly-provisioned device coming online. */
  deviceOnlinePolling: IDeviceOnlinePollingState | null;
  /** True after polling finishes without the device ever reporting is_online: true. */
  devicePollingTimedOut: boolean;
  /**
   * Set to true the moment the SSE stream fires a device_online event during
   * provisioning. Written unconditionally — even when assignedDevice is not
   * yet in Redux (device came online before the user pressed "Finish Setup").
   * Step 3 Verify reads this instead of assignedDevice.device_conn_status to
   * know the device is confirmed online. Cleared by startDeviceOnlinePolling
   * so a fresh provisioning run always starts with a clean signal.
   */
  sseDeviceOnlineReceived: boolean;
  /**
   * Per-device "recently confirmed online" cache: normalised device_id →
   * Date.now() of the last time we observed that device online (via the SSE
   * push or a cloud poll). Lets list screens that read connection status from
   * a slow REST endpoint (e.g. the Recent setups dashboard) optimistically
   * show a just-provisioned device as online while it's still running its
   * first-boot firmware OTA — during which `getDeviceConnectionStatus` lags
   * behind reality. Read with a freshness TTL so a stale entry can't pin a
   * truly-offline device "online" forever. NOT persisted — a live hint that
   * should reset on reload.
   */
  recentlyOnlineDevices: Record<string, number>;
  /**
   * True while the user is actively on any step of the device connection
   * flow (/connect-device). Used to pause background polling and SWR
   * revalidation that would waste bandwidth during Wi-Fi setup.
   */
  isDeviceSetupActive: boolean;
  /**
   * Set by an explicit user-initiated removal (Manage Device → Remove). While
   * armed, a device fetch returning this same device id is ignored so a stale
   * in-flight / lagging `fetchAssignedDevice` read can't resurrect the device
   * the user just removed. Cleared by a confirming fetch (empty / different /
   * status-0 device) or an explicit (re)assignment. NOT persisted — resets on
   * reload so it can never freeze a stale value.
   */
  recentlyRemovedDeviceId: string | null;
  /** Date.now() when the removal happened — enforces the suppression window. */
  recentlyRemovedAt: number | null;
}

const name = "clinic";

export const initialState: IClinicStateSlice = {
  clinicState: "pending",
  error: "",
  clinicList: [],
  userClinic: undefined,
  clinicFloorList: [],
  loadingFloors: [],
  selectedFloors: null,
  redirectToUserInfo: false,
  assignedDevice: null,
  deviceOfflineStreak: 0,
  deviceOnlinePolling: null,
  devicePollingTimedOut: false,
  sseDeviceOnlineReceived: false,
  recentlyOnlineDevices: {},
  isDeviceSetupActive: false,
  recentlyRemovedDeviceId: null,
  recentlyRemovedAt: null,
};

export const clinicSlice = createSlice({
  name,
  initialState,
  reducers: {
    setUserClinic(state, action: PayloadAction<{ clinic: IClinic }>) {
      state.userClinic = action.payload.clinic;
    },
    setRedirectToUserInfo(state, action: PayloadAction<boolean>) {
      state.redirectToUserInfo = action.payload;
    },
    setClinicList(state, action: PayloadAction<{ clinicList: IClinic[] }>) {
      state.clinicList = action.payload.clinicList;
    },
    setClinicFloorList(
      state,
      action: PayloadAction<{ clinicFloorList: IClinicFloor[] }>
    ) {
      state.clinicFloorList = action.payload.clinicFloorList;
      return state;
    },
    setSelectedFloors(state, action: PayloadAction<number[]>) {
      state.selectedFloors = action.payload;
    },
    setClinicState: (
      state,
      action: PayloadAction<Partial<IClinicStateSlice>>
    ) => ({
      ...state,
      ...action.payload,
    }),
    setAssignedDevice(state, action: PayloadAction<IAssignedDevice | null>) {
      state.assignedDevice = action.payload;
      // Any explicit set (assignment, disconnect, provisioning) is authoritative
      // and clears the debounce streak so the next poll starts fresh.
      state.deviceOfflineStreak = 0;
      // An explicit (re)assignment supersedes any pending removal suppression.
      state.recentlyRemovedDeviceId = null;
      state.recentlyRemovedAt = null;
    },
    /**
     * Explicit user-initiated removal (Manage Device → Remove). Clears the
     * device immediately and arms a suppression window so a stale in-flight or
     * backend-lagging fetch can't repopulate the just-removed device (the
     * "remove doesn't stick until restart" bug). Also stops any online-detection
     * polling that would otherwise re-set the device.
     */
    markDeviceRemoved(state, action: PayloadAction<{ deviceId: string }>) {
      state.assignedDevice = null;
      state.deviceOfflineStreak = 0;
      state.deviceOnlinePolling = null;
      state.devicePollingTimedOut = false;
      state.recentlyRemovedDeviceId = normalizeDeviceId(action.payload.deviceId);
      state.recentlyRemovedAt = Date.now();
    },
    /**
     * Applies the result of a `fetchAssignedDevice` call with debouncing that
     * prevents the two stale-state bugs behind false "device disconnected" UI:
     *
     *  1. Soft/transient failures (network blip, 5xx, timeout) resolve with
     *     `success: false` and no `data`. We MUST NOT overwrite a known-good
     *     device with `null` in that case — doing so wipes the device card and
     *     disables the metric cards until a full app restart re-bootstraps.
     *  2. A single transient `device_conn_status: 0` must not immediately flip
     *     a previously-connected device to "disconnected". We require
     *     `DEVICE_OFFLINE_CONFIRM_READS` consecutive offline reads first; until
     *     then the last-known "connected" status is preserved while every other
     *     field is still refreshed.
     *
     * `authoritative` (used by the cold-start bootstrap) skips the offline
     * debounce so startup reflects the true status immediately, but it still
     * never clobbers on a failed fetch.
     */
    applyDeviceFetchResult(
      state,
      action: PayloadAction<{
        success: boolean;
        device: IAssignedDevice | null;
        authoritative?: boolean;
      }>
    ) {
      const { success, device, authoritative } = action.payload;

      // (1) Failed fetch: keep the previous device + streak untouched.
      if (!success) return;

      // Successful fetch with no device → genuinely unassigned.
      if (!device) {
        state.assignedDevice = null;
        state.deviceOfflineStreak = 0;
        state.recentlyRemovedDeviceId = null;
        state.recentlyRemovedAt = null;
        return;
      }

      // A device returned with assignment status 0 is unassigned — treat it
      // exactly like an empty fetch. Without this, a backend that still returns
      // the just-removed row (status 0) would surface it as an offline device.
      if (device.status === 0) {
        state.assignedDevice = null;
        state.deviceOfflineStreak = 0;
        state.recentlyRemovedDeviceId = null;
        state.recentlyRemovedAt = null;
        return;
      }

      // Suppress a stale read that resurrects a device the user just removed: a
      // pre-removal in-flight fetch (or briefly-lagging backend) can resolve
      // after the removal and re-populate it. Ignore the matching device until
      // the window lapses; a confirming empty/status-0 fetch clears it sooner.
      if (
        state.recentlyRemovedDeviceId &&
        state.recentlyRemovedAt &&
        normalizeDeviceId(device.device_id) === state.recentlyRemovedDeviceId &&
        Date.now() - state.recentlyRemovedAt < DEVICE_REMOVAL_SUPPRESSION_MS
      ) {
        return;
      }

      // Any other resolved device supersedes a pending removal (a different
      // device was assigned, or the window lapsed).
      state.recentlyRemovedDeviceId = null;
      state.recentlyRemovedAt = null;

      // Online is always trusted immediately.
      if (device.device_conn_status === 1) {
        state.assignedDevice = device;
        state.deviceOfflineStreak = 0;
        return;
      }

      // Device reports offline.
      const prev = state.assignedDevice;
      const wasConnected = prev?.device_conn_status === 1;
      const streak = state.deviceOfflineStreak + 1;
      state.deviceOfflineStreak = streak;

      // (2) Debounce: hold the previous "connected" status during the grace
      // window so a transient offline read can't flip the UI. Skipped when the
      // caller is authoritative (cold-start bootstrap) or the device was not
      // previously connected (nothing good to preserve).
      if (
        !authoritative &&
        wasConnected &&
        streak < DEVICE_OFFLINE_CONFIRM_READS
      ) {
        state.assignedDevice = {
          ...device,
          device_conn_status: prev!.device_conn_status,
        };
        return;
      }

      state.assignedDevice = device;
    },
    startDeviceOnlinePolling(
      state,
      action: PayloadAction<{ deviceId: string; timeoutMs?: number }>
    ) {
      state.deviceOnlinePolling = {
        deviceId: action.payload.deviceId,
        startedAt: Date.now(),
        timeoutMs: action.payload.timeoutMs,
      };
      // Clear any previous timeout banner and SSE signal when a new poll starts
      // so a stale "online" from a prior provisioning run can't skip Step 3.
      state.devicePollingTimedOut = false;
      state.sseDeviceOnlineReceived = false;
    },
    stopDeviceOnlinePolling(state) {
      state.deviceOnlinePolling = null;
    },
    setDevicePollingTimedOut(state, action: PayloadAction<boolean>) {
      state.devicePollingTimedOut = action.payload;
    },
    /** Raised unconditionally when the SSE stream receives device_online — even
     *  before assignedDevice is populated in Redux (device came online before
     *  the tech pressed "Finish Setup"). Step 3 Verify subscribes to this flag. */
    setSseDeviceOnlineReceived(state, action: PayloadAction<boolean>) {
      state.sseDeviceOnlineReceived = action.payload;
    },
    /**
     * Records that a device was just observed online (SSE push or cloud poll),
     * keyed by normalised device_id. List screens overlay this onto a slow
     * REST status so a freshly-provisioned device (mid firmware OTA) shows
     * online immediately. Stored with a timestamp so readers can apply a TTL.
     */
    markDeviceOnlineLocally(
      state,
      action: PayloadAction<{ deviceId: string }>
    ) {
      const key = normalizeDeviceId(action.payload.deviceId);
      if (!key) return;
      state.recentlyOnlineDevices[key] = Date.now();
    },
    setDeviceSetupActive(state, action: PayloadAction<boolean>) {
      state.isDeviceSetupActive = action.payload;
    },
    setClinicSliceReset() {
      return initialState;
    },
  },
  extraReducers(builder) {},
});

export const {
  setUserClinic,
  setClinicList,
  setClinicFloorList,
  setSelectedFloors,
  setClinicState,
  setClinicSliceReset,
  setRedirectToUserInfo,
  setAssignedDevice,
  markDeviceRemoved,
  applyDeviceFetchResult,
  startDeviceOnlinePolling,
  stopDeviceOnlinePolling,
  setDevicePollingTimedOut,
  setSseDeviceOnlineReceived,
  markDeviceOnlineLocally,
  setDeviceSetupActive,
} = clinicSlice.actions;

const persistedReducer = persistReducer(
  {
    key: name,
    storage: storage,
    whitelist: ["selectedFloors", "clinicList", "userClinic", "assignedDevice"],
  },
  clinicSlice.reducer
);

export default persistedReducer;
